import {
  OperationBase,
  SwitchLedgerOperation,
  SwitchAssetOperation,
} from "@syla/shared/types/requests/BulkEditRequest";
import { produce } from "immer";
import React from "react";
import { IconType } from "react-icons";
import { FiClock, FiTag } from "react-icons/fi";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { IoWalletOutline } from "react-icons/io5";
import { SetOptional } from "type-fest";

export const bulkEditOperations: Record<
  Operation["type"],
  {
    label: string;
    description: string;
    icon: IconType;
    maxAmount?: number;
  }
> = {
  ledger: {
    label: "Change ledger classification",
    description: "Change the classification on matching ledgers.",
    icon: FiTag,
  },
  asset: {
    label: "Change asset",
    description: "Replace all occurrences of one asset with another.",
    icon: HiOutlineCurrencyDollar,
  },
  wallet: {
    label: "Change data source",
    description: "Move selected transactions to a different data source.",
    icon: IoWalletOutline,
  },
  time: {
    label: "Offset transaction dates",
    description: "Shift selected transaction dates by a set amount of time.",
    icon: FiClock,
    maxAmount: 1,
  },
};

export type SwitchLedgerOperationEntry = SetOptional<
  SwitchLedgerOperation,
  "from" | "to"
>;

export type SwitchAssetOperationEntry = SetOptional<
  SwitchAssetOperation,
  "from" | "to"
>;

export type ChangeWalletOperationEntry = SetOptional<
  ChangeWalletOperation,
  "from" | "to"
>;

export type AdjustTimeOperationEntry = SetOptional<
  AdjustTimeOperation,
  "direction" | "hours" | "minutes" | "seconds"
>;

export type OperationEntry =
  | SwitchLedgerOperationEntry
  | SwitchAssetOperationEntry
  | ChangeWalletOperationEntry
  | AdjustTimeOperationEntry;

export interface ChangeWalletOperation extends OperationBase {
  type: "wallet";
  from: string;
  to: string;
}

export interface AdjustTimeOperation extends OperationBase {
  type: "time";
  direction: "forward" | "back";
  hours: number;
  minutes: number;
  seconds: number;
}

export type Operation =
  | SwitchLedgerOperation
  | SwitchAssetOperation
  | ChangeWalletOperation
  | AdjustTimeOperation;

export const setOperation =
  <T extends OperationBase>(
    operation: T,
    setOperations: React.Dispatch<React.SetStateAction<T[]>>,
  ) =>
  (valueOrAction) => {
    setOperations(
      produce((operations) => {
        const opIndex = operations.findIndex((op) => op.id === operation.id);

        operations[opIndex] =
          typeof valueOrAction == "function" ?
            valueOrAction(operations[opIndex])
          : valueOrAction;
      }),
    );
  };
